import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { fmtDay, money, DT_TYPES } from "@/lib/daytime";
import { FamilyPicker } from "./DaytimeManualBook";

function PassDetail({ id, onClose, onChange }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get(`/daytime/admin/passes/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (action, extra = {}) => {
    try { await api.post(`/daytime/admin/passes/${id}/action`, { action, ...extra }); toast.success("Updated"); load(); onChange(); } catch (e) { toast.error(apiErr(e)); }
  };
  const ask = (q, fn) => { const v = window.prompt(q); if (v) fn(v); };
  if (!d) return null;
  const p = d.pass;
  const rock = p.visits_total > 0;
  return (
    <div className="space-y-3" data-testid="dt-pass-detail">
      <p className="text-sm text-slate-600"><b>{p.label}</b> · {p.explorers.map((e) => e.name).join(" & ") || "any family Explorer"} · {money(p.amount)} {p.paid ? "paid" : "unpaid"}</p>
      <p className="text-sm text-slate-600">Purchased {fmtDay(p.created_at.slice(0, 10))} · valid {fmtDay(p.start)} – {fmtDay(p.end)} · <span className="capitalize">{p.effective_status}</span></p>
      {rock && <p className="text-sm text-slate-600">Visits used {p.visits_used} / {p.visits_total} · remaining {p.visits_remaining} · booked {p.reserved}</p>}
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="outline" onClick={() => ask("Extend by how many days?", (v) => act("extend", { days: Number(v) }))} data-testid="dt-act-extend">Extend</Button>
        <Button size="sm" variant="outline" onClick={() => ask("New expiration date (YYYY-MM-DD)", (v) => act("set_end", { end: v }))} data-testid="dt-act-setend">Change expiry</Button>
        {rock && <Button size="sm" variant="outline" onClick={() => act("add_visit", { count: 1 })} data-testid="dt-act-addvisit">+1 comp visit</Button>}
        {rock && <Button size="sm" variant="outline" onClick={() => act("remove_visit", { count: 1 })} data-testid="dt-act-removevisit">−1 visit</Button>}
        {p.status === "frozen" ? <Button size="sm" variant="outline" onClick={() => act("unfreeze")} data-testid="dt-act-unfreeze">Unfreeze</Button>
          : <Button size="sm" variant="outline" onClick={() => act("freeze")} data-testid="dt-act-freeze">Freeze</Button>}
        <Button size="sm" variant="outline" onClick={() => ask("Upgrade to: pass5, pass10, monthly or family", (v) => act("upgrade", { kind: v.trim() }))} data-testid="dt-act-upgrade">Upgrade</Button>
        {!p.paid && <Button size="sm" variant="outline" onClick={() => act("mark_paid")} data-testid="dt-act-paid">Mark paid</Button>}
        <Button size="sm" variant="outline" className="text-coral" onClick={() => window.confirm("Cancel this pass and its upcoming bookings?") && act("cancel")} data-testid="dt-act-cancel">Cancel</Button>
      </div>
      <div>
        <p className="font-bold text-navy text-sm mb-1">Bookings & attendance</p>
        <div className="max-h-56 overflow-y-auto space-y-1">{d.bookings.map((b) => (
          <p key={b.id} className="text-xs flex justify-between bg-slate-50 rounded-lg px-2 py-1"><span>{fmtDay(b.date)} · {b.child_name}</span><span className="capitalize">{b.status.replace("_", " ")}</span></p>
        ))}{d.bookings.length === 0 && <p className="text-xs text-slate-400">No bookings yet.</p>}</div>
      </div>
      <Button variant="ghost" onClick={onClose}>Close</Button>
    </div>
  );
}

function SellPass({ onDone }) {
  const [fam, setFam] = useState(null);
  const [kind, setKind] = useState("pass5");
  const [kids, setKids] = useState([]);
  const [credit, setCredit] = useState("");
  const sell = async () => {
    if (!fam) return toast.error("Pick a family");
    try { const { data } = await api.post("/daytime/admin/purchase", { parent_id: fam.id, kind, child_ids: kids }); toast.success(`${data.pass.label} created${data.order ? ` · ${money(data.order.amount)} due (ref ${data.order.reference})` : ""}`); onDone(); } catch (e) { toast.error(apiErr(e)); }
  };
  const give = async () => {
    if (!fam || !Number(credit)) return toast.error("Pick a family and amount");
    try { const { data } = await api.post("/daytime/admin/credit", { parent_id: fam.id, amount: Number(credit) }); toast.success(`Credit issued · balance ${money(data.balance)}`); setCredit(""); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3 h-fit" data-testid="dt-sell-pass">
      <p className="font-display font-extrabold text-navy">Add a pass / issue credit</p>
      <FamilyPicker value={fam} onPick={(p) => { setFam(p); setKids([]); }} />
      <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full border rounded-xl h-10 px-2 text-sm" data-testid="dt-sell-kind">
        {["pass5", "pass10", "monthly", "family"].map((k) => <option key={k} value={k}>{DT_TYPES[k]}</option>)}
      </select>
      {fam && <div className="flex flex-wrap gap-2">{fam.explorers.map((e) => (
        <button key={e.id} onClick={() => setKids(kids.includes(e.id) ? kids.filter((x) => x !== e.id) : [...kids, e.id])} className={`px-3 py-1 rounded-full border text-sm ${kids.includes(e.id) ? "bg-navy text-white" : ""}`} data-testid={`dt-sell-child-${e.id}`}>{e.name}</button>))}</div>}
      <Button onClick={sell} className="w-full rounded-full bg-turquoise text-navy font-bold" data-testid="dt-sell-submit">Create pass (pending payment)</Button>
      <div className="flex gap-2"><Input type="number" value={credit} onChange={(e) => setCredit(e.target.value)} placeholder="Credit $" className="rounded-xl" data-testid="dt-credit-amount" />
        <Button onClick={give} variant="outline" className="rounded-full" data-testid="dt-credit-submit">Issue credit</Button></div>
    </div>
  );
}

export function DaytimeMembers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => { api.get("/daytime/admin/passes").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const shown = rows.filter((p) => !q || `${p.parent_name} ${p.parent_email} ${p.explorers.map((e) => e.name).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="grid lg:grid-cols-[1.6fr_1fr] gap-5 mt-3">
      <div className="space-y-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parent or Explorer" className="rounded-xl" data-testid="dt-members-search" />
        {shown.map((p) => (
          <button key={p.id} onClick={() => setOpen(p.id)} className="w-full text-left bg-white border border-slate-200 rounded-2xl p-3" data-testid={`dt-member-${p.id}`}>
            <div className="flex justify-between gap-2"><p className="font-bold text-navy">{p.parent_name} · {p.label}</p>
              <span><Badge variant="outline" className="capitalize">{p.effective_status}</Badge> {!p.paid && <Badge className="bg-coral text-white">Unpaid</Badge>}</span></div>
            <p className="text-xs text-slate-500">{p.explorers.map((e) => e.name).join(" & ") || "family visits"} · bought {fmtDay(p.created_at.slice(0, 10))} · {money(p.amount)} · {fmtDay(p.start)} – {fmtDay(p.end)}
              {p.visits_total ? ` · ${p.visits_used}/${p.visits_total} used, ${p.visits_remaining} left` : ""} · {p.upcoming} upcoming · {p.attended} attended</p>
          </button>
        ))}
        {shown.length === 0 && <p className="text-sm text-slate-400">No Daytime passes or memberships yet.</p>}
      </div>
      <SellPass onDone={load} />
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle className="font-display text-navy">Manage pass</DialogTitle></DialogHeader>
          {open && <PassDetail id={open} onClose={() => setOpen(null)} onChange={load} />}</DialogContent>
      </Dialog>
    </div>
  );
}
